import * as React from "react";
import { flexRender, type Row } from "@tanstack/react-table";
import { cn } from "../../lib/utils";
import { Checkbox } from "../Checkbox";
import { tableCellVariants } from "./tableVariants";

interface TableBodyProps<TData> {
  rows: Row<TData>[];
  enableRowSelection: boolean;
  size: "small" | "medium" | "large";
  variant: "default" | "bordered" | "striped";
  showRowHover: boolean;
  cellClassName?: string;
  onRowClick?: (row: TData) => void;
  getRowClassName: (row: TData) => string;
  handleRowClick: (row: TData) => void;
  getColumnStyle: (
    columnId: string,
    fallback?: { width?: number; minWidth?: number; maxWidth?: number }
  ) => React.CSSProperties;
}

interface CellState {
  rowId: string;
  cellId: string;
}

export function TableBody<TData>({
  rows,
  enableRowSelection,
  size,
  variant,
  showRowHover,
  cellClassName,
  onRowClick,
  getRowClassName,
  handleRowClick,
  getColumnStyle,
}: TableBodyProps<TData>) {
  const [focusedCell, setFocusedCell] = React.useState<CellState | null>(null);
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);

  return (
    <tbody className={cn("bg-surface-fill-neutral-intense")}>
      {rows.map((row) => {
        const isRowHovered = hoveredRow === row.id;
        const handleClick = () => handleRowClick(row.original);

        return (
          <tr
            key={row.id}
            className={cn(
              variant === "striped" &&
                row.index % 2 === 1 &&
                "bg-surface-fill-neutral-moderate",
              onRowClick && "cursor-pointer",
              isRowHovered &&
                showRowHover &&
                "bg-surface-fill-neutral-moderate",
              getRowClassName(row.original)
            )}
            onClick={handleClick}
            onMouseEnter={() => setHoveredRow(row.id)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            {enableRowSelection && (
              <td
                className={cn(
                  tableCellVariants({ size }),
                  "w-10",
                  cellClassName
                )}
                style={{
                  paddingTop: "20px",
                  paddingBottom: "20px",
                }}
              >
                <Checkbox
                  checked={row.getIsSelected()}
                  isIndeterminate={row.getIsSomeSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select row ${row.id}`}
                />
              </td>
            )}
            {row.getVisibleCells().map((cell, cellIndex) => {
              const isCellFocused =
                focusedCell?.rowId === row.id &&
                focusedCell?.cellId === cell.id;
              const cellState = isCellFocused ? "focus" : "default";
              const visibleCells = row.getVisibleCells();
              const isFirstCell = cellIndex === 0;
              const isLastCell = cellIndex === visibleCells.length - 1;

              return (
                <td
                  key={cell.id}
                  className={cn(
                    tableCellVariants({ size, state: cellState }),
                    cell.column.columnDef.meta?.cellClassName,
                    cellClassName
                  )}
                  style={{
                    ...getColumnStyle(cell.column.id, {
                      width: cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                      maxWidth: cell.column.columnDef.maxSize,
                    }),
                    paddingTop: "20px",
                    paddingBottom: "20px",
                    ...(isFirstCell && { paddingLeft: "24px" }),
                    ...(isLastCell && { paddingRight: "24px" }),
                  }}
                  tabIndex={0}
                  onFocus={() =>
                    setFocusedCell({ rowId: row.id, cellId: cell.id })
                  }
                  onBlur={() => setFocusedCell(null)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
